import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCurrentParticipant, useCurrentUser, useLogout } from '@/hooks/useAuth';

export default function ProfilePage() {
  const { data: user, isLoading } = useCurrentUser();
  // The full record carries the centro habitual; the normalised User does not.
  const { data: participant } = useCurrentParticipant();
  const logout = useLogout();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login'),
    });
  };

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Perfil | AICD</title>
        </Helmet>
        <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
          <div className="text-muted-foreground">A carregar…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Perfil | AICD</title>
      </Helmet>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-black text-foreground">Perfil</h1>
        </div>

        {/* User info */}
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-2xl font-semibold text-primary">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{user?.name}</h2>
              <p className="text-muted-foreground">{user?.email}</p>
              {participant?.digitalCenterName && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Centro habitual: {participant.digitalCenterName}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="card overflow-hidden">
          <div className="divide-y divide-border">
            <button
              onClick={() => navigate('/profile/settings')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-accent active:bg-accent/80"
              aria-label="Ir para definições da conta"
            >
              <span className="text-foreground">Definições da conta</span>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/profile/badges')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-accent active:bg-accent/80"
              aria-label="Ver os meus badges"
            >
              <span className="text-foreground">Badges</span>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/notifications')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-accent active:bg-accent/80"
              aria-label="Ver notificações"
            >
              <span className="text-foreground">Notificações</span>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/privacidade')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-accent active:bg-accent/80"
              aria-label="Ir para privacidade e os seus dados"
            >
              <span className="text-foreground">Privacidade e os seus dados</span>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              className="w-full p-4 flex items-center justify-between text-left hover:bg-accent active:bg-accent/80"
              aria-label="Ir para ajuda e suporte"
            >
              <span className="text-foreground">Ajuda e suporte</span>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          disabled={logout.isPending}
          className="w-full p-4 text-destructive font-medium bg-card rounded-lg border border-border hover:bg-destructive/10 active:bg-destructive/20 transition-colors"
        >
          {logout.isPending ? 'A terminar sessão…' : 'Terminar sessão'}
        </button>

        {/* Version */}
        <p className="text-center text-sm text-muted-foreground/60">Versão 1.0.0</p>
      </div>
    </>
  );
}
